import { isSnipeProtectedListing } from './secure-purchase'
import { MAX_PROTECTED_ITEMS_PER_PURCHASE } from './secure-ticket-client'

type CheckoutStepKind = 'legacy' | 'secure'

export interface CheckoutStep {
	kind: CheckoutStepKind
	ids: string[]
}

/**
 * Partition a cart into the purchases it actually needs.
 *
 * Standard listings settle together in one bulk purchase, exactly as before.
 * Protected listings settle together too, as one chained purchase of sale
 * packages (a passthrough parent plus a sale child per item, with at most one
 * shared setup transaction), but they cannot share a transaction with standard
 * listings, so a mixed cart is two purchases: standard first, then protected.
 * Very large protected carts split at the per-purchase item limit.
 *
 * Order within a step follows the caller's order.
 */
export function planCheckout<T extends { id: string }>(
	items: ReadonlyArray<T | null | undefined>
): CheckoutStep[] {
	const legacy: string[] = []
	const secure: string[] = []
	const seen = new Set<string>()
	for (const item of items) {
		if (!item || seen.has(item.id)) continue
		seen.add(item.id)
		;(isSnipeProtectedListing(item) ? secure : legacy).push(item.id)
	}
	const steps: CheckoutStep[] = []
	if (legacy.length > 0) steps.push({ kind: 'legacy', ids: legacy })
	for (let start = 0; start < secure.length; start += MAX_PROTECTED_ITEMS_PER_PURCHASE) {
		steps.push({ kind: 'secure', ids: secure.slice(start, start + MAX_PROTECTED_ITEMS_PER_PURCHASE) })
	}
	return steps
}

/** True when the cart cannot settle in a single purchase. */
export function isMultiStepCheckout(steps: ReadonlyArray<CheckoutStep>): boolean {
	return steps.length > 1
}
